import type { Conversation, Project } from "@lumen/shared";

export type ProjectWithConversations = Project & {
  conversations: Conversation[];
};

/** Apply local expansion writes over a server snapshot that may be stale. */
export function mergeProjectExpansionState(
  projects: ReadonlyArray<Pick<ProjectWithConversations, "id" | "expanded">>,
  pending: ReadonlyMap<string, boolean>,
): Set<string> {
  const expanded = new Set(
    projects.filter((project) => project.expanded).map((project) => project.id),
  );
  for (const [projectId, isExpanded] of pending) {
    if (isExpanded) expanded.add(projectId);
    else expanded.delete(projectId);
  }
  return expanded;
}

type UnknownRecord = Record<string, unknown>;
const isRecord = (value: unknown): value is UnknownRecord =>
  Boolean(value) && typeof value === "object";

const conversationSortKey = (conversation: Conversation) => {
  const timestamp = Date.parse(conversation.updatedAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export function sortProjectConversations(
  conversations: Conversation[],
): Conversation[] {
  return [...conversations].sort(
    (a, b) =>
      conversationSortKey(b) - conversationSortKey(a) ||
      a.id.localeCompare(b.id),
  );
}

/** Normalize the server list before it enters the sidebar state. */
export function normalizeProjectRows(
  value: unknown,
): ProjectWithConversations[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!isRecord(candidate)) return [];
    if (typeof candidate.id !== "string" || !candidate.id) return [];
    if (typeof candidate.name !== "string" || !candidate.name) return [];
    const conversations = Array.isArray(candidate.conversations)
      ? candidate.conversations.filter(
          (conversation): conversation is Conversation =>
            isRecord(conversation) &&
            typeof conversation.id === "string" &&
            typeof conversation.title === "string",
        )
      : [];
    return [
      {
        ...(candidate as unknown as Project),
        description:
          typeof candidate.description === "string"
            ? candidate.description
            : null,
        visibility:
          candidate.visibility === "organization" ? "organization" : "private",
        defaultActivity: candidate.defaultActivity ?? "ask",
        composerDefaults: isRecord(candidate.composerDefaults)
          ? candidate.composerDefaults
          : {},
        conversations: sortProjectConversations(conversations),
      } as ProjectWithConversations,
    ];
  });
}

export function canEditProject(
  project: Project,
  user: { id: string; organizationPermissions: string[] },
) {
  return (
    project.ownerUserId === user.id ||
    user.organizationPermissions.includes("projects:manage")
  );
}
